/**
 * Premium Homepage Component
 * ==========================
 * 100% CMS-driven - NO hardcoded content
 * All text, images, and configuration from database
 */

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useBlueprint } from '../../context/BlueprintContext';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { 
  ArrowRight, Play, Search, Box, Layers, Users, TrendingUp, 
  CheckCircle, ChevronRight, Linkedin, Instagram, Youtube
} from 'lucide-react';

// Icon mapping for CMS icons
const ICON_MAP = {
  'cube': Box,
  'box': Box,
  'layers': Layers,
  'users': Users,
  'trending-up': TrendingUp,
  'search': Search,
  'check-circle': CheckCircle,
  'arrow-right': ArrowRight,
  'play': Play,
  'linkedin': Linkedin,
  'instagram': Instagram,
  'youtube': Youtube,
};

const getIcon = (iconName, className = "w-6 h-6") => {
  const IconComponent = ICON_MAP[iconName] || Box;
  return <IconComponent className={className} />;
};

// =============================================================================
// HERO SECTION
// =============================================================================
const HeroSection = ({ data }) => {
  const navigate = useNavigate();
  
  if (!data) return null;
  
  return (
    <section className="relative bg-[#F5F2EE] overflow-hidden">
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[85vh]">
          {/* Left Content */}
          <div className="flex flex-col justify-center px-6 md:px-12 lg:px-16 py-16 lg:py-24">
            {/* Title */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-light leading-[1.1] mb-6">
              <span className="block text-[#1a1a1a]">{data.title_line1}</span>
              <span className="block text-[#b08b5c]">{data.title_line2}</span>
              <span className="block text-[#b08b5c]">{data.title_line3}</span>
            </h1>
            
            {/* Subtitle */}
            <p className="text-lg md:text-xl text-[#666] max-w-lg mb-10">
              {data.subtitle}
            </p>
            
            {/* CTAs */}
            <div className="flex flex-wrap gap-4">
              {data.cta_primary && (
                <button
                  onClick={() => navigate(data.cta_primary.route)}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#b08b5c] text-white rounded-full hover:bg-[#96754d] transition-colors font-medium"
                >
                  {data.cta_primary.text}
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
              
              {data.cta_secondary && (
                <button
                  onClick={() => navigate(data.cta_secondary.route)}
                  className="inline-flex items-center gap-2 px-6 py-3 border border-[#1a1a1a] text-[#1a1a1a] rounded-full hover:bg-[#1a1a1a] hover:text-white transition-colors font-medium"
                >
                  {data.cta_secondary.text}
                  <Play className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          
          {/* Right Image */}
          <div className="relative lg:absolute lg:right-0 lg:top-0 lg:w-1/2 lg:h-full">
            <img
              src={data.image}
              alt={data.image_alt || 'SlabReality'}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// =============================================================================
// FEATURES BAR
// =============================================================================
const FeaturesBar = ({ data }) => {
  if (!data?.items) return null;
  
  return (
    <section className="bg-white border-y border-[#e5e5e5]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {data.items.map((item, index) => (
            <div key={item.id || index} className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center border border-[#b08b5c] rounded-lg text-[#b08b5c]">
                {getIcon(item.icon, "w-5 h-5")}
              </div>
              <div>
                <h3 className="font-semibold text-sm tracking-wide text-[#1a1a1a] mb-1">
                  {item.title}
                </h3>
                <p className="text-sm text-[#666] leading-snug">
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// =============================================================================
// ROLES SECTION
// =============================================================================
const RolesSection = ({ data }) => {
  const navigate = useNavigate();
  
  if (!data?.cards) return null;
  
  // Helper to render title with highlight
  const renderTitle = () => {
    if (!data.title_highlight) return data.title;
    
    const parts = data.title.split(data.title_highlight);
    return (
      <>
        {parts[0]}
        <span className="italic font-serif">{data.title_highlight}</span>
        {parts[1]}
      </>
    );
  };
  
  return (
    <section className="py-20 md:py-28 bg-[#F5F2EE]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-xs tracking-[0.3em] text-[#b08b5c] uppercase mb-4">
            {data.label}
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-[#1a1a1a]">
            {renderTitle()}
          </h2>
        </div>
        
        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {data.cards.map((card, index) => (
            <div 
              key={card.id || index}
              className="group relative overflow-hidden rounded-2xl bg-[#1a1a1a] aspect-[3/4] cursor-pointer"
              onClick={() => navigate(card.cta_route)}
            >
              {/* Image */}
              <img
                src={card.image}
                alt={card.title}
                className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-40 group-hover:scale-105 transition-all duration-500"
              />
              
              {/* Content */}
              <div className="absolute inset-0 p-6 flex flex-col justify-end">
                <p className="text-[#b08b5c] text-xs tracking-wider mb-1">
                  {card.for_label}
                </p>
                <h3 className="text-white text-xl font-medium mb-3">
                  {card.title}
                </h3>
                <p className="text-white/70 text-sm mb-4 line-clamp-2">
                  {card.description}
                </p>
                <div className="flex items-center gap-2 text-[#b08b5c] text-sm font-medium group-hover:gap-3 transition-all">
                  {card.cta_text}
                  <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// =============================================================================
// EXPERIENCE SECTION (5 Steps)
// =============================================================================
const ExperienceSection = ({ data }) => {
  if (!data?.steps) return null;
  
  const renderTitle = () => {
    if (!data.title_highlight) return data.title;
    
    const parts = data.title.split(data.title_highlight);
    return (
      <>
        {parts[0]}
        <span className="italic font-serif">{data.title_highlight}</span>
        {parts[1]}
      </>
    );
  };
  
  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-xs tracking-[0.3em] text-[#b08b5c] uppercase mb-4">
            {data.label}
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-[#1a1a1a]">
            {renderTitle()}
          </h2>
        </div>
        
        {/* Steps */}
        <div className="relative">
          {/* Connection Line */}
          <div className="hidden md:block absolute top-8 left-0 right-0 h-[2px] bg-[#e5e5e5]" style={{ left: '10%', right: '10%' }} />
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            {data.steps.map((step, index) => (
              <div key={step.number || index} className="relative text-center">
                {/* Icon */}
                <div className="relative z-10 w-16 h-16 mx-auto mb-6 rounded-full border-2 border-[#e5e5e5] bg-white flex items-center justify-center text-[#b08b5c]">
                  {getIcon(step.icon, "w-6 h-6")}
                </div>
                
                {/* Arrow between steps */}
                {index < data.steps.length - 1 && (
                  <div className="hidden md:block absolute top-8 -right-4 z-20">
                    <ArrowRight className="w-4 h-4 text-[#ccc]" />
                  </div>
                )}
                
                {/* Number */}
                <p className="text-[#b08b5c] text-sm font-medium mb-2">
                  {step.number}
                </p>
                
                {/* Title */}
                <h3 className="text-lg font-medium text-[#1a1a1a] mb-2">
                  {step.title}
                </h3>
                
                {/* Description */}
                <p className="text-sm text-[#666]">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// =============================================================================
// FEATURED MATERIAL SECTION
// =============================================================================
const FeaturedMaterialSection = ({ data }) => {
  const navigate = useNavigate();
  
  if (!data) return null;
  
  return (
    <section className="py-20 md:py-28 bg-[#F5F2EE]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* Left - Featured Material */}
          <div className="relative overflow-hidden rounded-2xl bg-[#1a1a1a] aspect-[4/3]">
            <img
              src={data.image}
              alt={data.material_name}
              className="absolute inset-0 w-full h-full object-cover opacity-80"
            />
            <div className="absolute inset-0 p-8 flex flex-col justify-end bg-gradient-to-t from-black/80 via-transparent to-transparent">
              <p className="text-[#b08b5c] text-xs tracking-wider mb-2">
                {data.label}
              </p>
              <h3 className="text-white text-3xl md:text-4xl font-light mb-3">
                {data.material_name}
              </h3>
              <p className="text-white/70 mb-6 max-w-md">
                {data.description}
              </p>
              <button
                onClick={() => navigate(data.cta_route)}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#b08b5c] text-white rounded-full hover:bg-[#96754d] transition-colors text-sm font-medium w-fit"
              >
                {data.cta_text}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {/* Right - Project Gallery */}
          <div className="grid grid-cols-2 gap-4">
            {data.projects?.slice(0, 4).map((project, index) => (
              <div 
                key={index}
                className="relative overflow-hidden rounded-xl aspect-[4/3] group cursor-pointer"
              >
                <img
                  src={project.image}
                  alt={project.title}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h4 className="text-white font-medium text-sm">{project.title}</h4>
                    <p className="text-white/70 text-xs">{project.location}</p>
                    <p className="text-[#b08b5c] text-xs">{project.room_type}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// =============================================================================
// FOOTER SECTION
// =============================================================================
const FooterSection = ({ data, branding }) => {
  if (!data) return null;
  
  return (
    <footer className="bg-[#1a1a1a] text-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12 mb-12">
          {/* Logo & Tagline */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-2xl font-light tracking-wider">SR</span>
              <span className="text-sm tracking-wider text-[#b08b5c]">SLABREALITY</span>
            </div>
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              {data.tagline}
            </p>
            {/* Social Icons */}
            <div className="flex gap-3">
              {data.social?.map((item, index) => (
                <a
                  key={index}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:border-[#b08b5c] hover:text-[#b08b5c] transition-colors"
                  aria-label={item.label}
                >
                  {getIcon(item.icon, "w-4 h-4")}
                </a>
              ))}
            </div>
          </div>
          
          {/* Link Columns */}
          {data.columns?.map((column, index) => (
            <div key={index}>
              <h4 className="text-xs tracking-wider text-white/40 uppercase mb-4">
                {column.title}
              </h4>
              <ul className="space-y-3">
                {column.links?.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <Link
                      to={link.route}
                      className="text-sm text-white/70 hover:text-[#b08b5c] transition-colors"
                    >
                      {link.text}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          
          {/* Newsletter */}
          {data.newsletter && (
            <div className="lg:col-span-2 lg:col-start-5">
              <h4 className="text-lg font-medium mb-2">{data.newsletter.title}</h4>
              <p className="text-white/60 text-sm mb-4">{data.newsletter.description}</p>
              <div className="flex">
                <input
                  type="email"
                  placeholder={data.newsletter.placeholder}
                  className="flex-1 px-4 py-2.5 bg-white/10 border border-white/20 rounded-l-lg text-sm focus:outline-none focus:border-[#b08b5c]"
                />
                <button className="px-4 py-2.5 bg-[#b08b5c] rounded-r-lg hover:bg-[#96754d] transition-colors">
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-sm">{data.copyright}</p>
          <div className="flex gap-6">
            {data.legal_links?.map((link, index) => (
              <Link
                key={index}
                to={link.route}
                className="text-white/40 text-sm hover:text-white/70 transition-colors"
              >
                {link.text}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

// =============================================================================
// MAIN HOMEPAGE COMPONENT
// =============================================================================
const PremiumHomePage = () => {
  const { config, loading } = useBlueprint();
  
  // Extract data from CMS
  const home = config?.ui?.home || {};
  const footer = config?.footer?.config || {};
  const branding = config?.branding || {};
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F2EE] flex items-center justify-center">
        <div className="animate-pulse">
          <div className="w-16 h-16 border-2 border-[#b08b5c] rounded-full" />
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <HeroSection data={home.hero} />
        <FeaturesBar data={home.features_bar} />
        <RolesSection data={home.roles} />
        <ExperienceSection data={home.experience} />
        <FeaturedMaterialSection data={home.featured_material} />
      </main>
      <Footer />
    </div>
  );
};

export default PremiumHomePage;
